import { COURSE_PAYMENT_URLS } from "@shared/course.utils";
import { MemberUtils } from "@shared/member.utils";
import type { ReactNode } from "react";
import styled from "styled-components";
import type { Season } from "../../stores/season.store";
import type { CourseSubscription, Member } from "../../stores/user.store";
import { getMemberIcon, getMemberTypeLabel } from "../../styles/member";
import {
  MemberCard,
  MemberContent,
  MemberInfo,
  MemberName,
  MemberTypeLabel,
  MembershipInfo,
  MembershipLabel,
  MembershipStatus,
} from "./components/MemberCard";
import { CoursePaymentNotice } from "./CoursePaymentNotice";

const PaymentLayout = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 16px;

  @media (max-width: 600px) {
    flex-direction: column;
    align-items: stretch;
  }
`;

const PaymentDetails = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
`;

const Hint = styled.span`
  font-size: 14px;
`;

const PayLink = styled.a`
  flex-shrink: 0;
  background: #000;
  color: #fff;
  padding: 12px 20px;
  border-radius: 4px;
  font-weight: bold;
  text-decoration: none;

  &:hover {
    opacity: 0.9;
  }

  @media (max-width: 600px) {
    text-align: center;
  }
`;

export function CoursePendingIntro() {
  return (
    <div id="iscrizioni-in-attesa">
      <CoursePaymentNotice stage="recap" />
    </div>
  );
}

export function CoursePaymentLayout({
  member,
  subscription,
  children,
}: {
  member: Member;
  subscription?: CourseSubscription;
  children: ReactNode;
}) {
  if (subscription?.status !== "pending") {
    return <>{children}</>;
  }

  return (
    <PaymentLayout>
      <PaymentDetails>
        {children}
        <Hint>Sul sito dei maestri inserisci <strong>{member.first_name} {member.last_name}</strong> e paga.</Hint>
      </PaymentDetails>
      <PayLink href={COURSE_PAYMENT_URLS[subscription.type]} target="_blank" rel="noopener noreferrer">
        PAGA CORSO {subscription.type === "ski" ? "SCI" : "SNOWBOARD"} ↗
      </PayLink>
    </PaymentLayout>
  );
}

export function CoursePendingRecap({
  members,
  subscriptions,
  season,
}: {
  members: Member[];
  subscriptions: CourseSubscription[];
  season: Season;
}) {
  if (subscriptions.length === 0) {
    return null;
  }

  return (
    <>
      <CoursePendingIntro />
      {subscriptions.map((subscription) => {
        const member = members.find((item) => item.id === subscription.subscriber_id);
        if (!member) {
          return null;
        }
        const memberType = MemberUtils.getMemberType(member.date_of_birth, season);
        const companion = members.find((item) => item.id === subscription.companion_id);
        return (
          <MemberCard key={subscription.id}>
            <MemberContent>
              <MemberInfo>
                <MemberName>{member.first_name} {member.last_name}</MemberName>
                <MemberTypeLabel>{getMemberTypeLabel(memberType)}</MemberTypeLabel>
              </MemberInfo>
              <img src={getMemberIcon(memberType)} alt={getMemberTypeLabel(memberType)} width="40" height="40" />
            </MemberContent>
            <MembershipInfo>
              <CoursePaymentLayout member={member} subscription={subscription}>
                <MembershipLabel>
                  {subscription.type === "ski" ? "SCI" : "SNOWBOARD"} • LIVELLO {subscription.level}
                </MembershipLabel>
                {companion && <MembershipLabel>Accomp: {companion.first_name}</MembershipLabel>}
                <MembershipStatus $status="pending">In attesa di verifica</MembershipStatus>
              </CoursePaymentLayout>
            </MembershipInfo>
          </MemberCard>
        );
      })}
    </>
  );
}
